// sic!

import classNames from "classnames";
import { addDays, differenceInDays, isSameDay, subDays } from "date-fns";
import React from "react";
import type { FirebaseTimestamp } from "../../common/types/FirebaseTimestamp";
import { capitalize } from "../../common/utils/string.util";
import {
    formatDateComparison,
    formatDefaultDate,
    formatDefaultDateTime,
    formatDefaultTime,
    formatTimeComparison,
    formatToRelativeAndAbsoluteLabel,
} from "../../common/utils/time.formatted.util";
import { fromTimestampToDateOptional } from "../../utils/timestamp.util";

type Props = {
    className?: string;
    date?: Date;
    now?: Date;
    timestamp?: FirebaseTimestamp;
};

type PropsWithFormatter = {
    formatter: (date: Date, now: Date) => string;
} & Props;

function DateTimeBase(props: PropsWithFormatter) {
    const {
        className,
        date: dateFromProps,
        now: nowFromProps,
        timestamp,
        formatter,
    } = props;

    const timestampAsDate = fromTimestampToDateOptional(timestamp);
    const date = dateFromProps || timestampAsDate;

    if (!date) {
        return <span />;
    }

    const now = nowFromProps || new Date();
    const formattedDate = formatter(date, now);
    return <span className={className}>{formattedDate}</span>;
}

function DefaultDate(props: Props) {
    return DateTimeBase({
        ...props,
        formatter: formatDefaultDate,
    });
}

function DefaultTime(props: Props) {
    return DateTimeBase({
        ...props,
        formatter: formatDefaultTime,
    });
}

function DefaultDateTime(props: Props) {
    return DateTimeBase({
        ...props,
        formatter: formatDefaultDateTime,
    });
}

function DateComparison(props: Props) {
    return DateTimeBase({
        ...props,
        formatter: formatDateComparison,
    });
}

function TimeComparison(props: Props) {
    return DateTimeBase({
        ...props,
        formatter: formatTimeComparison,
    });
}

function DateTimeComparisonUpTo30Days(props: Props) {
    return DateTimeBase({
        ...props,
        formatter: (date: Date, now: Date) => {
            if (Math.abs(differenceInDays(date, now)) <= 30) {
                return capitalize(
                    `${formatDateComparison(date, now)}, ${formatDefaultTime(
                        date,
                        now,
                    )}`,
                );
            }
            return formatDefaultDateTime(date, now);
        },
    });
}

function DateTimeComparison(props: Props) {
    return DateTimeBase({
        ...props,
        formatter: (date: Date, now: Date) =>
            capitalize(
                `${formatDateComparison(date, now)}, ${formatDefaultTime(date, now)}`,
            ),
    });
}

function DateTimePrettyTimestamp(props: Props) {
    return DateTimeBase({
        ...props,
        formatter: (date: Date, now: Date) => {
            if (isSameDay(date, now)) {
                return `Today ${formatDefaultTime(date, now)}`;
            }
            const tomorrow = addDays(now, 1);
            if (isSameDay(date, tomorrow)) {
                return `Tomorrow ${formatDefaultTime(date, now)}`;
            }
            const yesterday = subDays(now, 1);
            if (isSameDay(date, yesterday)) {
                return `Yesterday ${formatDefaultTime(date, now)}`;
            }
            return formatDefaultDateTime(date, now);
        },
    });
}

function RelativeAndAbsoluteDateTime(props: Props) {
    const { className, date: dateFromProps, now: nowFromProps, timestamp } = props;

    const timestampAsDate = fromTimestampToDateOptional(timestamp);
    const date = dateFromProps || timestampAsDate;
    if (!date) {
        return <span />;
    }

    const now = nowFromProps || new Date();
    const formattedDate = formatToRelativeAndAbsoluteLabel(date, now);
    return (
        <span className={classNames("date-time-label", className)}>
            <span className="date-time-label--relative">{formattedDate[0]}</span>
            <span className="date-time-label--absolute">{formattedDate[1]}</span>
        </span>
    );
}

export const FormattedTime = {
    Default: DefaultTime,
    Comparison: TimeComparison,
};

export const FormattedDate = {
    Default: DefaultDate,
    Comparison: DateComparison,
};

export const FormattedDateTime = {
    Default: DefaultDateTime,
    Comparison: DateTimeComparison,
    ComparisonUpTo30Days: DateTimeComparisonUpTo30Days,
    PrettyTimestamp: DateTimePrettyTimestamp,
    RelativeAndAbsoluteDateTime,
};
